"use client";

import { useLanguage } from "@/contexts/LanguageContext";


type PaginationProps = {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  totalItems: number;
  startIndex: number;
  endIndex: number;
  itemLabel?: string;
};

export function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  totalItems,
  startIndex,
  endIndex,
  itemLabel = "items",
}: PaginationProps) {
  const { t } = useLanguage();
  if (totalItems <= 0) return null;
  const showPageControls = totalPages > 1;

  const getPageNumbers = (): (number | "...")[] => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const set = new Set<number>([1, totalPages]);
    for (let i = Math.max(1, currentPage - 1); i <= Math.min(totalPages, currentPage + 1); i++) {
      set.add(i);
    }
    const sorted = [...set].sort((a, b) => a - b);
    const pages: (number | "...")[] = [];
    let prev = 0;
    for (const p of sorted) {
      if (p > prev + 1) pages.push("...");
      pages.push(p);
      prev = p;
    }
    return pages;
  };

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-10 pt-8 border-t border-[#E5E5E5]">
      <p className="text-sm text-[#6B7280]">
        {t("pagination.showing")} {startIndex + 1}-{endIndex} {t("pagination.of")} {totalItems} {itemLabel}
      </p>
      {showPageControls && (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage <= 1}
            className="px-3 py-2 rounded-lg border border-[#E5E5E5] text-sm font-medium text-[#130F0E] hover:bg-[#F4F4F4] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-colors"
          >
            {t("pagination.previous")}
          </button>
          <div className="flex items-center gap-1">
            {getPageNumbers().map((page, i) =>
              page === "..." ? (
                <span key={`ellipsis-${i}`} className="px-2 text-[#6B7280]">
                  …
                </span>
              ) : (
                <button
                  key={page}
                  type="button"
                  onClick={() => onPageChange(page)}
                  className={`min-w-[36px] h-9 px-2 rounded-lg text-sm font-medium transition-colors ${
                    currentPage === page
                      ? "bg-[#BD2226] text-white"
                      : "border border-[#E5E5E5] text-[#130F0E] hover:bg-[#F4F4F4] hover:border-[#DD0428]/50"
                  }`}
                >
                  {page}
                </button>
              )
            )}
          </div>
          <button
            type="button"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage >= totalPages}
            className="px-3 py-2 rounded-lg border border-[#E5E5E5] text-sm font-medium text-[#130F0E] hover:bg-[#F4F4F4] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-colors"
          >
            {t("pagination.next")}
          </button>
        </div>
      )}
    </div>
  );
}
